import CreateButton from "../components/CreateButton"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import Dropzone from "react-dropzone"
import { Helmet, HelmetProvider } from "react-helmet-async"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import { v4 } from "uuid"
import api from "../api"
import { ReactComponent as AddIcon } from "../assets/images/icons/ic-add.svg"
import { ReactComponent as FilterIcon } from "../assets/images/icons/ic-filter.svg"
import { ReactComponent as EmptyIllustration } from "../assets/images/illustrations/il-empty.svg"
import { ReactComponent as InsertDocumentIllustration } from "../assets/images/illustrations/il-insert-document.svg"
import { Each } from "../common/Each"
import Button from "../components/Button"
import Dialog from "../components/dialogs/Dialog"
import EmptyContent from "../components/EmptyContent"
import HeaderFooterLayout from "../components/layouts/HeaderFooterLayout"
import Loader from "../components/Loader"
import MaterialButton from "../components/MaterialButton"
import Pagination from "../components/Pagination"
import Select from "../components/Select"
import Table from "../components/Table"
import TextInput from "../components/TextInput"
import Card from "../components/cards/Card"
import styles from "./Exercises.module.css"
import { ButtonStatus } from "../common/constants"

const Exercises = () => {

    const { t } = useTranslation()
    const navigate = useNavigate()

    const [exercises, setExercises] = useState([])
    const [loading, setLoading] = useState(false)
    const [filterOpen, setFilterOpen] = useState(false)

    const [selectedCourseFilter, setSelectedCourseFilter] = useState(null)
    const [selectedEditionFilter, setSelectedEditionFilter] = useState(null)
    const [selectedModuleFilter, setSelectedModuleFilter] = useState(null)
    const [selectedLessonFilter, setSelectedLessonFilter] = useState(null)
    const [courseFilterSearch, setCourseFilterSearch] = useState("")
    const [editionFilterSearch, setEditionFilterSearch] = useState("")
    const [moduleFilterSearch, setModuleFilterSearch] = useState("")
    const [lessonFilterSearch, setLessonFilterSearch] = useState("")
    const [filterEditions, setFilterEditions] = useState([])
    const [filterModules, setFilterModules] = useState([])
    const [filterLessons, setFilterLessons] = useState([])
    const [searchQuery, setSearchQuery] = useState("")
    const [totalExercises, setTotalExercises] = useState(0)
    const [page, setPage] = useState(0)
    const per = 15

    const [openDialog, setOpenDialog] = useState(false)
    const [courses, setCourses] = useState([])
    const [editions, setEditions] = useState([])
    const [modules, setModules] = useState([])
    const [lessons, setLessons] = useState([])

    const [course, setCourse] = useState(null)
    const [edition, setEdition] = useState(null)
    const [name, setName] = useState("")
    const [description, setDescription] = useState("")
    const [expiresAt, setExpiresAt] = useState(null)
    const [module, setModule] = useState(null)
    const [lesson, setLesson] = useState(null)
    const [courseDialogSearch, setCourseDialogSearch] = useState("")
    const [editionDialogSearch, setEditionDialogSearch] = useState("")
    const [moduleDialogSearch, setModuleDialogSearch] = useState("")
    const [lessonDialogSearch, setLessonDialogSearch] = useState("")
    const [files, setFiles] = useState([])

    const [currentExercise, setCurrentExercise] = useState(null)

    const [formError, setFormError] = useState("")
    const [waitingAdd, setWaitingAdd] = useState(false)
    const [waitingEdit, setWaitingEdit] = useState(null)

    const dropzoneRef = useRef(null)

    const getCourses = useCallback(async () => {
        try {
            const courses = await api.get("/admin/courses");
            setCourses(Array.from(
                courses.reduce((map, c) => {
                    if (!map.has(c.id)) {
                        map.set(c.id, { ...c, label: c.name });
                    }
                    return map;
                }, new Map()).values()
            ))
        } catch (e) {
            console.error(e)
        }
    }, [])

    const getEditions = useCallback(async () => {
        if (!course?.id) {
            setEditions([])
            return
        }

        try {
            let editions = await api.get(`/admin/courses/${course.id}/editions`);
            editions = editions.filter(e => e.modules && e.modules.length > 0)
            setEditions(editions.map(e => ({ ...e, label: e.name })))
        } catch (e) {
            console.error(e)
        }
    }, [course])

    const getExercises = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams()
            params.append("page", page)
            params.append("per", per)

            if (searchQuery && searchQuery.trim() !== "") {
                params.append("q", searchQuery.trim())
            }

            if (selectedCourseFilter?.id) {
                params.append("course_id", selectedCourseFilter.id)
            }

            if (selectedEditionFilter?.id) {
                params.append("edition_id", selectedEditionFilter.id)
            }

            if (selectedModuleFilter?.id) {
                params.append("module_id", selectedModuleFilter.id)
            }

            if (selectedLessonFilter?.id) {
                params.append("lesson_id", selectedLessonFilter.id)
            }

            const data = await api.get(`/admin/exercises?${params.toString()}`)
            setExercises(data.exercises ?? [])
            setTotalExercises(data.count ?? 0)
        } catch (e) {
            console.error(e);
            setExercises([])
            setTotalExercises(0)
        }
        setLoading(false);
    }, [page, per, searchQuery, selectedCourseFilter, selectedEditionFilter, selectedModuleFilter, selectedLessonFilter])

    useEffect(() => {
        getExercises();
    }, [getExercises]);

    useEffect(() => {
        getCourses()
    }, [getCourses])

    useEffect(() => {
        if (course) {
            getEditions()
            if (currentExercise) {
                if (course.id !== currentExercise.course.id) {
                    setCurrentExercise(c => ({ ...c, course, edition: null, module: null, lesson: null }))
                    setEditions([])
                    setModules([])
                    setLessons([])
                    setEdition(null)
                    setModule(null)
                    setLesson(null)
                }
            }
        } else {
            setEditions([])
            setModules([])
            setLessons([])
        }
    }, [course, currentExercise, getEditions])


    useEffect(() => {
        if (edition) {
            setModules(edition.modules.map(m => ({ ...m, label: m.name })))
        } else {
            setModules([])
            setLessons([])
        }
    }, [edition])

    useEffect(() => {
        if (currentExercise) {
            setName(currentExercise.name)
            setDescription(currentExercise.description)
            setExpiresAt(currentExercise.expiresAt)
            const c = courses.find(cc => cc.id === currentExercise.course.id)
            setCourse(c)
        }

    }, [courses, currentExercise])

    useEffect(() => {
        if (editions.length > 0 && currentExercise && !edition) {
            const e = editions.find(ee => ee.id === currentExercise.edition?.id)
            setEdition(e)
            setOpenDialog(true)
        }
    }, [currentExercise, edition, editions])

    useEffect(() => {
        if (modules.length > 0 && currentExercise && !module) {
            setModule(modules.find(m => m.id === currentExercise.module?.id))
        }
    }, [currentExercise, module, modules])

    useEffect(() => {
        if (lessons.length > 0 && currentExercise?.lesson && !lesson) {
            setLesson(lessons.find(l => l.id === currentExercise.lesson?.id))
        }
    }, [currentExercise, lesson, lessons])

    useEffect(() => {
        if (module) {
            setLessons(module.lessons.map(l => ({ ...l, label: l.name })))
        } else {
            setLessons([])
        }
    }, [module])

    useEffect(() => {
        if (!openDialog) {
            onDialogClose()
        }
    }, [openDialog])

    useEffect(() => {
        const loadFilterEditions = async () => {
            if (!selectedCourseFilter?.id) {
                setFilterEditions([])
                return
            }

            try {
                let editions = await api.get(`/admin/courses/${selectedCourseFilter.id}/editions`)
                editions = editions.filter((item) => item.modules && item.modules.length > 0)
                setFilterEditions(editions.map((item) => ({ ...item, label: item.name })))
            } catch (e) {
                console.error(e)
                setFilterEditions([])
            }
        }

        loadFilterEditions()
    }, [selectedCourseFilter])

    useEffect(() => {
        if (!selectedEditionFilter) {
            setFilterModules([])
            return
        }

        setFilterModules((selectedEditionFilter.modules ?? []).map((m) => ({ ...m, label: m.name })))
    }, [selectedEditionFilter])

    useEffect(() => {
        if (!selectedModuleFilter) {
            setFilterLessons([])
            return
        }

        setFilterLessons((selectedModuleFilter.lessons ?? []).map((l) => ({ ...l, label: l.name })))
    }, [selectedModuleFilter])

    useEffect(() => {
        setPage(0)
    }, [searchQuery, selectedCourseFilter, selectedEditionFilter, selectedModuleFilter, selectedLessonFilter])

    const filterBySearch = useCallback((options = [], search = "") => {
        const normalizedSearch = (search ?? "").toLowerCase().trim()
        if (!normalizedSearch) {
            return options
        }

        return options.filter((option) => {
            const optionLabel = `${option?.name ?? option?.label ?? ""}`.toLowerCase()
            return optionLabel.includes(normalizedSearch)
        })
    }, [])

    const courseFilterOptions = useMemo(() => {
        const searchableCourses = filterBySearch(
            [...courses].sort((a, b) => a.name.localeCompare(b.name)),
            courseFilterSearch
        )

        return [
            { id: "__all_courses__", name: "Tutti i corsi", label: "Tutti i corsi" },
            ...searchableCourses,
        ]
    }, [courses, courseFilterSearch, filterBySearch])

    const editionFilterOptions = useMemo(() => {
        const searchableEditions = filterBySearch(
            [...filterEditions].sort((a, b) => a.name.localeCompare(b.name)),
            editionFilterSearch
        )

        return [
            { id: "__all_editions__", name: "Tutte le edizioni", label: "Tutte le edizioni" },
            ...searchableEditions,
        ]
    }, [filterEditions, editionFilterSearch, filterBySearch])

    const moduleFilterOptions = useMemo(() => {
        const searchableModules = filterBySearch(
            [...filterModules].sort((a, b) => a.name.localeCompare(b.name)),
            moduleFilterSearch
        )

        return [
            { id: "__all_modules__", name: "Tutti i moduli", label: "Tutti i moduli" },
            ...searchableModules,
        ]
    }, [filterModules, moduleFilterSearch, filterBySearch])

    const lessonFilterOptions = useMemo(() => {
        const searchableLessons = filterBySearch(
            [...filterLessons].sort((a, b) => a.name.localeCompare(b.name)),
            lessonFilterSearch
        )

        return [
            { id: "__all_lessons__", name: "Tutte le lezioni", label: "Tutte le lezioni" },
            ...searchableLessons,
        ]
    }, [filterLessons, lessonFilterSearch, filterBySearch])

    const dialogCourseOptions = useMemo(() => {
        return filterBySearch(
            [...courses].sort((a, b) => a.name.localeCompare(b.name)),
            courseDialogSearch
        )
    }, [courses, courseDialogSearch, filterBySearch])

    const dialogEditionOptions = useMemo(() => {
        return filterBySearch(
            [...editions].sort((a, b) => a.name.localeCompare(b.name)),
            editionDialogSearch
        )
    }, [editions, editionDialogSearch, filterBySearch])

    const dialogModuleOptions = useMemo(() => {
        return filterBySearch(
            [...modules].sort((a, b) => a.name.localeCompare(b.name)),
            moduleDialogSearch
        )
    }, [modules, moduleDialogSearch, filterBySearch])

    const dialogLessonOptions = useMemo(() => {
        return filterBySearch(lessons, lessonDialogSearch)
    }, [lessons, lessonDialogSearch, filterBySearch])

    const onSearch = (searchText) => {
        setSearchQuery(searchText ?? "")
    }

    const save = () => {
        if (currentExercise) {
            edit()
        } else {
            add()
        }
    }

    const edit = async () => {
        setWaitingEdit(ButtonStatus.Loading)
        setFormError("")
        const newExercise = {
            name,
            module_id: module.id,
        }
        if (description) {
            newExercise.description = description
        }
        if (lesson !== null) {
            newExercise.lesson_id = lesson.id
        }
        if (expiresAt) {
            newExercise.expires_at = new Date(expiresAt).toISOString()
        }
        try {
            await api.put(`/admin/exercises/${currentExercise.id}`, newExercise);
            getExercises()
            setOpenDialog(false)
        } catch (e) {
            console.error(e)
            setWaitingEdit(ButtonStatus.Error)
            setFormError("Impossibile salvare le modifiche. Riprova.")
        }
    }

    const add = async () => {
        setWaitingAdd(true)
        setFormError("")
        const form = new FormData()
        form.append("name", name)
        form.append("module_id", module.id)

        if (description) {
            form.append("description", description)
        }
        if (lesson !== null) {
            form.append("lesson_id", lesson.id)
        }
        if (expiresAt) {
            form.append("expires_at", new Date(expiresAt).toISOString())
        }


        for (let i = 0; i < files.length; i++) {
            form.append(`files`, files[i])
        }
        try {
            await api.post("/admin/exercises", form, {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            })
            await getExercises()
            setOpenDialog(false)
        } catch (e) {
            console.error(e)
            setFormError("Impossibile creare l’esercizio. Riprova: i dati inseriti sono stati mantenuti.")
        }
        setWaitingAdd(false)
    }
    const onDialogClose = () => {
        setFormError("")
        setCourse(null)
        setEdition(null)
        setName("")
        setDescription("")
        setExpiresAt(null)
        setModule(null)
        setLesson(null)
        setCourseDialogSearch("")
        setEditionDialogSearch("")
        setModuleDialogSearch("")
        setLessonDialogSearch("")
        setFiles([])
        setCurrentExercise(null)
        setWaitingEdit(null)
        setOpenDialog(false)
    }

    return (
        <HeaderFooterLayout>
            <HelmetProvider>
                <Helmet>
                    <title>Didattica - Esercizi</title>
                </Helmet>
            </HelmetProvider>
            <div className={styles.container}>
                <div className={styles.section}>
                    <div className={styles.sectionInner}>
                        <div className={styles.content}>

                            <div className={styles.header}>
                                <div><p className={styles.eyebrow}>Didattica</p><h1>Esercizi</h1><p>Gestisci le attività didattiche e consulta le consegne degli studenti.</p></div>
                                <div className={styles.add}>
                                    <CreateButton onClick={() => setOpenDialog(true)}>
                                        Nuovo esercizio

                                    </CreateButton>
                                </div>
                            </div>
                            <Card appearance="modern" integratedTable title="Elenco esercizi" actions={<span className={styles.count}>{loading ? 'Caricamento…' : `${totalExercises} esercizi`}</span>}>
                            <div className={styles.filterSection}>
                                <div className={styles.search}>
                                    <TextInput appearance="modern"
                                        onKeyUp={onSearch}
                                        type="search"
                                        placeholder={"Ricerca"}
                                        style={{
                                            backgroundColor: "var(--background-secondary-color)",
                                        }}
                                    />

                                    <button type="button" className={styles.filterButton} aria-expanded={filterOpen} aria-controls="exercise-filters" onClick={() => setFilterOpen(open => !open)}><FilterIcon aria-hidden="true" />Filtri</button>
                                </div>

                                <div id="exercise-filters" className={styles.filters} hidden={!filterOpen}>
                                    <Select appearance="modern" portal
                                        placeholder={"Corso"}
                                        selected={selectedCourseFilter ?? courseFilterOptions[0]}
                                        options={courseFilterOptions}
                                        onSearch={(value) => setCourseFilterSearch(value ?? "")}
                                        onSelect={(item) => {
                                            if (!item || item.id === "__all_courses__") {
                                                setSelectedCourseFilter(null)
                                                setSelectedEditionFilter(null)
                                                setSelectedModuleFilter(null)
                                                setSelectedLessonFilter(null)
                                                setEditionFilterSearch("")
                                                setModuleFilterSearch("")
                                                setLessonFilterSearch("")
                                                return
                                            }

                                            setSelectedCourseFilter(item)
                                            setSelectedEditionFilter(null)
                                            setSelectedModuleFilter(null)
                                            setSelectedLessonFilter(null)
                                            setEditionFilterSearch("")
                                            setModuleFilterSearch("")
                                            setLessonFilterSearch("")
                                        }}
                                    />

                                    <Select appearance="modern" portal
                                        disabled={!selectedCourseFilter}
                                        placeholder={"Edizione"}
                                        selected={selectedEditionFilter ?? editionFilterOptions[0]}
                                        options={editionFilterOptions}
                                        onSearch={(value) => setEditionFilterSearch(value ?? "")}
                                        onSelect={(item) => {
                                            if (!item || item.id === "__all_editions__") {
                                                setSelectedEditionFilter(null)
                                                setSelectedModuleFilter(null)
                                                setSelectedLessonFilter(null)
                                                setModuleFilterSearch("")
                                                setLessonFilterSearch("")
                                                return
                                            }

                                            setSelectedEditionFilter(item)
                                            setSelectedModuleFilter(null)
                                            setSelectedLessonFilter(null)
                                            setModuleFilterSearch("")
                                            setLessonFilterSearch("")
                                        }}
                                    />

                                    <Select appearance="modern" portal
                                        disabled={!selectedEditionFilter}
                                        placeholder={"Modulo"}
                                        selected={selectedModuleFilter ?? moduleFilterOptions[0]}
                                        options={moduleFilterOptions}
                                        onSearch={(value) => setModuleFilterSearch(value ?? "")}
                                        onSelect={(item) => {
                                            if (!item || item.id === "__all_modules__") {
                                                setSelectedModuleFilter(null)
                                                setSelectedLessonFilter(null)
                                                setLessonFilterSearch("")
                                                return
                                            }

                                            setSelectedModuleFilter(item)
                                            setSelectedLessonFilter(null)
                                            setLessonFilterSearch("")
                                        }}
                                    />

                                    <Select appearance="modern" portal
                                        disabled={!selectedModuleFilter}
                                        placeholder={"Lezione"}
                                        selected={selectedLessonFilter ?? lessonFilterOptions[0]}
                                        options={lessonFilterOptions}
                                        onSearch={(value) => setLessonFilterSearch(value ?? "")}
                                        onSelect={(item) => {
                                            if (!item || item.id === "__all_lessons__") {
                                                setSelectedLessonFilter(null)
                                                return
                                            }

                                            setSelectedLessonFilter(item)
                                        }}
                                    />
                                </div>
                            </div>

                            <div className={styles.exercises}>
                                {
                                    loading === false && exercises.length > 0 &&
                                    <Table
                                        integrated
                                        appearance="minimal"
                                        onRowClick={(exercise) => {
                                            if (exercise?.id) {
                                                navigate(`/exercises/${exercise.id}`)
                                            }
                                        }}
                                        columns={[
                                            { key: "name", label: t("exercises.name"), type: "custom", wrap: true },
                                            { key: "course_name", label: "Corso", type: "custom", wrap: true },
                                            { key: "edition_name", label: "Edizione", type: "custom", wrap: true },
                                            { key: "module_name", label: "Modulo", type: "custom", wrap: true },
                                            { key: "lesson_name", label: "Lezione", type: "custom", wrap: true },
                                            { key: "materials_count", label: t("exercises.materials"), type: "number" },
                                            { key: "submit_count", label: t("exercises.submitNumber"), type: "string" },
                                            { key: "expires_at", label: t("exercises.expiresAt"), type: "datetime" },
                                        ]}
                                        elements={exercises.map((exercise) => ({
                                            ...exercise,
                                            name: {
                                                value: exercise.name ?? "-",
                                                type: "string",
                                                component: <div className={styles.clampedCell}>{exercise.name ?? "-"}</div>,
                                            },
                                            course_name: {
                                                value: exercise.course?.name ?? "-",
                                                type: "string",
                                                component: <div className={styles.clampedCell}>{exercise.course?.name ?? "-"}</div>,
                                            },
                                            edition_name: {
                                                value: exercise.edition?.name ?? "-",
                                                type: "string",
                                                component: <div className={styles.clampedCell}>{exercise.edition?.name ?? "-"}</div>,
                                            },
                                            module_name: {
                                                value: exercise.module?.name ?? "-",
                                                type: "string",
                                                component: <div className={styles.clampedCell}>{exercise.module?.name ?? "-"}</div>,
                                            },
                                            lesson_name: {
                                                value: exercise.lesson?.name ?? "-",
                                                type: "string",
                                                component: <div className={styles.clampedCell}>{exercise.lesson?.name ?? "-"}</div>,
                                            },
                                            materials_count: exercise.medias?.length ?? 0,
                                            submit_count: `${exercise.users_exercises_count ?? 0} / ${exercise.total_users ?? exercise.users ?? 0}`,
                                            href: `/exercises/${exercise.id}`,
                                        }))}
                                    />
                                }

                                {
                                    loading === true &&
                                    <Table
                                        integrated
                                        loading
                                        loadingN={8}
                                        appearance="minimal"
                                        columns={[
                                            { key: "name", label: t("exercises.name"), type: "string" },
                                            { key: "course_name", label: "Corso", type: "string" },
                                            { key: "edition_name", label: "Edizione", type: "string" },
                                            { key: "module_name", label: "Modulo", type: "string" },
                                        ]}
                                    />
                                }

                                {
                                    loading === false && exercises.length === 0 &&
                                    <EmptyContent
                                        Illustration={EmptyIllustration}
                                        title={"Nessun esercizio trovato"}
                                        message={
                                            <div style={{ display: "flex", alignItems: "center", }}>
                                                <Button
                                                    appearance="text"
                                                    onClick={() => setOpenDialog(true)}
                                                    style={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: ".2rem"
                                                    }}
                                                >
                                                    <AddIcon />
                                                    Aggiungi
                                                </Button>
                                            </div>
                                        }
                                    />
                                }
                            </div>
                            {
                                !loading && totalExercises > 0 &&
                                <div className={styles.pagination}><Pagination
                                    total={totalExercises}
                                    per={per}
                                    page={page}
                                    onChange={(p) => setPage(p)}
                                /></div>
                            }
                            </Card>
                        </div>
                    </div>

                    <Dialog appearance="modern"
                        open={openDialog}
                        title={currentExercise ? "Modifica esercizio" : "Crea esercizio"}
                        style={{ width: "720px", maxWidth: "calc(100vw - 32px)" }}
                        contentMaxHeight="78vh"
                        onClose={() => { if (!waitingAdd && waitingEdit !== ButtonStatus.Loading) onDialogClose() }}
                        action={
                            <Button
                                additionalClass={styles.primaryButton}
                                onClick={save}
                                status={waitingAdd ? ButtonStatus.Loading : waitingEdit}
                                disabled={!course || !edition || !module || !name.trim() || waitingAdd || waitingEdit === ButtonStatus.Loading}
                            >
                                {currentExercise ? "Salva modifiche" : "Crea esercizio"}
                            </Button>
                        }
                    >
                        <div className={styles.dialog}>
                            {formError && <div role="alert" className={styles.formError}>{formError}</div>}
                            <p className={styles.formHint}>Indica nome, corso, edizione e modulo. Lezione, scadenza e materiali sono facoltativi.</p>
                            <TextInput appearance="modern"
                                type="text" label="Nome esercizio"
                                placeholder={t("exercises.name")}
                                onKeyUp={(s) => {
                                    setName(s)
                                }}
                                value={name}
                            />

                            <TextInput appearance="modern"
                                type="textarea" label="Descrizione"
                                placeholder={t("exercises.description")}
                                value={description}
                                onKeyUp={(value) => setDescription(value)}
                                style={{ minHeight: "96px" }}
                            />

                            <div className={styles.formField}><span>Corso</span><Select appearance="modern" portal
                                disabled={courses.length === 0}
                                placeholder={"Corso"}
                                selected={courses.find((c) => c.id === course?.id) ?? null}
                                options={dialogCourseOptions}
                                onSearch={(value) => setCourseDialogSearch(value ?? "")}
                                onSelect={(item) => {
                                    if (item?.id) {
                                        setCourse(item)
                                        setEdition(null)
                                        setModule(null)
                                        setLesson(null)
                                        setEditionDialogSearch("")
                                        setModuleDialogSearch("")
                                        setLessonDialogSearch("")
                                    }
                                }}
                            /></div>

                            <div className={styles.formField}><span>Edizione</span><Select appearance="modern" portal
                                disabled={editions.length === 0}
                                placeholder={"Edizione"}
                                selected={editions.find((e) => e.id === edition?.id) ?? null}
                                options={dialogEditionOptions}
                                onSearch={(value) => setEditionDialogSearch(value ?? "")}
                                onSelect={(item) => {
                                    if (item?.id) {
                                        setEdition(item)
                                        setModule(null)
                                        setLesson(null)
                                        setModuleDialogSearch("")
                                        setLessonDialogSearch("")
                                    }
                                }}
                            /></div>

                            <div className={styles.formField}><span>Modulo</span><Select appearance="modern" portal
                                disabled={modules.length === 0}
                                placeholder={"Modulo"}
                                selected={modules.find((m) => m.id === module?.id) ?? null}
                                options={dialogModuleOptions}
                                onSearch={(value) => setModuleDialogSearch(value ?? "")}
                                onSelect={(item) => {
                                    if (item?.id) {
                                        setModule(item)
                                        setLesson(null)
                                        setLessonDialogSearch("")
                                    }
                                }}
                            /></div>

                            <div className={styles.formField}><span>Lezione (facoltativa)</span><Select appearance="modern" portal
                                disabled={lessons.length === 0}
                                placeholder={"Lezione"}
                                selected={lessons.find((l) => l.id === lesson?.id) ?? null}
                                options={dialogLessonOptions}
                                onSearch={(value) => setLessonDialogSearch(value ?? "")}
                                onSelect={(item) => {
                                    if (item?.id) {
                                        setLesson(item)
                                    }
                                }}
                            /></div>

                            <TextInput appearance="modern"
                                type="date" label="Scadenza (facoltativa)"
                                value={expiresAt ? new Date(expiresAt + 'Z') : null}
                                defaultValue={currentExercise?.expires_at ? new Date(currentExercise?.expires_at + 'Z') : null}
                                placeholder={t("exercises.expiresAt")}
                                onKeyUp={(d) => setExpiresAt(d ? d.toISOString().split('.')[0] : null)}
                            />
                            {
                                !currentExercise &&
                                <div className={styles.dropzoneContainer}>
                                    <div className={styles.subtitle} >
                                        Materiali
                                        {files.length > 0 &&
                                            <AddIcon style={{ cursor: "pointer", color: "var(--tertiary)" }} onClick={() => {
                                                if (dropzoneRef && dropzoneRef.current) {
                                                    dropzoneRef.current.click();
                                                }
                                            }} />
                                        }
                                    </div>
                                    {
                                        files.length > 0 && waitingAdd === false &&
                                        <div className={styles.filesContainer}>
                                            <div className={styles.files}>
                                                <Each
                                                    of={files}
                                                    render={(file) => (
                                                        <div className={styles.file}>
                                                            <MaterialButton modern thumbnail
                                                                material={file}
                                                                onClick={() => {
                                                                    setFiles((prevFiles) => {
                                                                        const idx = prevFiles.findIndex(f => f.id === file.id)
                                                                        if (idx > -1) prevFiles.splice(idx, 1)
                                                                        return [...prevFiles]
                                                                    })
                                                                }}
                                                            />
                                                        </div>
                                                    )}
                                                />
                                            </div>
                                        </div>
                                    }

                                    <div style={{
                                        display: files.length === 0 && waitingAdd === false ? "flex" : "none",
                                    }}>
                                        <Dropzone
                                            onDrop={(newFiles) => {
                                                newFiles.forEach(f => f.id = v4())
                                                setFiles([...files, ...newFiles])
                                            }}>
                                            {({ getRootProps, getInputProps }) => (
                                                <section style={{ display: 'flex', width: '100%', padding: 0, margin: 0 }}>
                                                    <div {...getRootProps()} style={{ display: 'flex', width: '100%' }} ref={dropzoneRef} >
                                                        <input {...getInputProps()} />
                                                        <div className={styles.dropzone}>
                                                            <InsertDocumentIllustration />
                                                            <div className={styles.dropzoneLabel}>
                                                                {t("materials.dragAndDrop")}
                                                            </div>
                                                            <Button
                                                                style={{ marginTop: '.5rem', padding: '0.6rem 2rem' }}
                                                                additionalClass={styles.blueButton}
                                                                onClick={() => dropzoneRef.current?.click()}
                                                            >
                                                                {t("materials.selectFile").toUpperCase()}
                                                            </Button>
                                                        </div>
                                                    </div>
                                                </section>
                                            )}
                                        </Dropzone>
                                    </div>
                                    {
                                        waitingAdd === true &&
                                        <div className={styles.loader}>
                                            <Loader />
                                        </div>
                                    }
                                </div>
                            }
                        </div>
                    </Dialog>
                </div>
            </div>

        </HeaderFooterLayout>
    )
}


export default Exercises
